import React from 'react';
import { Stack, Grid, Chip, TextField, Box, Typography } from '@mui/material';
import { PRESET_DURATIONS } from '@features/appointments';

interface DurationSchedulePickerProps {
  date: string;
  startTime: string;
  endTime: string;
  selectedDuration: number;
  isCustomDuration: boolean;
  onDateChange: (date: string) => void;
  onStartTimeChange: (time: string) => void;
  onEndTimeChange: (time: string) => void;
  onDurationSelect: (minutes: number) => void;
  onCustomDurationSelect: () => void;
  title?: string;
  description?: string;
  slimDurations?: Array<{ label: string; minutes: number }>;
}

export const DurationSchedulePicker: React.FC<DurationSchedulePickerProps> = ({
  date,
  startTime,
  endTime,
  selectedDuration,
  isCustomDuration,
  onDateChange,
  onStartTimeChange,
  onEndTimeChange,
  onDurationSelect,
  onCustomDurationSelect,
  slimDurations,
}) => {
  // Use passed slim durations or fallback to defaults
  const durations = slimDurations || [
    { label: '30m', minutes: 30 },
    { label: '45m', minutes: 45 },
    { label: '1h', minutes: 60 },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Compact date and time inputs */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
        }}
      >
        <TextField
          label="Date"
          type="date"
          value={date}
          onChange={e => onDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{
            flex: 1.2,
            minWidth: '140px',
          }}
        />
        <TextField
          label={isCustomDuration ? 'Start' : 'Time'}
          type="time"
          value={startTime}
          onChange={e => onStartTimeChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{
            flex: 1,
            minWidth: '100px',
          }}
        />
        {isCustomDuration && (
          <TextField
            label="End"
            type="time"
            value={endTime}
            onChange={e => onEndTimeChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{
              flex: 1,
              minWidth: '100px',
            }}
          />
        )}
      </Box>

      {/* Compact duration chips */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1, minWidth: 'fit-content' }}>
          Duration:
        </Typography>
        {durations.map(duration => (
          <Chip
            key={duration.minutes}
            label={duration.label}
            onClick={() => onDurationSelect(duration.minutes)}
            color={
              selectedDuration === duration.minutes && !isCustomDuration ? 'primary' : 'default'
            }
            variant={
              selectedDuration === duration.minutes && !isCustomDuration ? 'filled' : 'outlined'
            }
            size="small"
          />
        ))}
        <Chip
          label="Other"
          onClick={onCustomDurationSelect}
          color={isCustomDuration ? 'primary' : 'default'}
          variant={isCustomDuration ? 'filled' : 'outlined'}
          size="small"
        />
      </Box>
    </Box>
  );
};

export default DurationSchedulePicker;
